import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, NavLink as RouterLink, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Link } from '@mui/material';

import { useNotValidatedClasses } from 'queries/NotValidatedClasses';
import Loading from 'components/Loading';
import DashboardLayout from 'layouts/dashboard';
import getUserRole from 'utils/jwt/getUserRole';
import logout from 'utils/logout';
import checkAccess from 'utils/roles/checkAccess';
import { PublicRouter } from './PublicRouter';
import { PrivateRouter } from './PrivateRouter';
import { PATH_DASHBOARD } from './paths';
import { General, Reports, Settings, Shop } from './Modules';

const Login = lazy(() => import('pages/auth/Login'));
const Reset = lazy(() => import('pages/auth/Reset'));
const NotFound = lazy(() => import('pages/Page404'));

export default function AppRouter() {
  const { enqueueSnackbar } = useSnackbar();
  const notValidatedClasses = useNotValidatedClasses();
  const role = getUserRole();
  const location = useLocation();
  const accessToken = localStorage.getItem('accessToken');

  const defaultNavigate = () => {
    if (checkAccess([0]) || checkAccess([1]) || checkAccess([2])) {
      return <Navigate to={`general${PATH_DASHBOARD.registeredUsers.root}`} />;
    }

    if (checkAccess([4])) {
      return <Navigate to={PATH_DASHBOARD.vendors.goProductsVendorAdmin} />;
    }

    if (checkAccess([3])) {
      return <Navigate to={`general${PATH_DASHBOARD.facility.root}`} />;
    }

    if (checkAccess([5])) {
      return <Navigate to={`general${PATH_DASHBOARD.challenges.root}`} />;
    }

    if (role && accessToken) {
      enqueueSnackbar('Role is not valid', { variant: 'error' });
      logout();
    }
  };

  useEffect(() => {
    if (notValidatedClasses?.classes.length && location.pathname !== PATH_DASHBOARD.notValidatedClasses.root) {
      enqueueSnackbar(
        <>
          There are non-updated classes, go to the
          <Link sx={{ mx: 0.5 }} component={RouterLink} to={PATH_DASHBOARD.notValidatedClasses.link}>
            <strong> Not Validated Classes</strong>
          </Link>
          and validate them
        </>,
        {
          variant: 'warning',
        }
      );
    }
  }, [notValidatedClasses?.classes]);

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route
          path="/"
          element={
            <PrivateRouter>
              <DashboardLayout />
            </PrivateRouter>
          }
        >
          <Route index element={defaultNavigate()} />
          <Route path="general/*" element={<General />} />
          <Route path="settings/*" element={<Settings />} />
          <Route path="shop/*" element={<Shop />} />
          <Route path="reports/*" element={<Reports />} />
        </Route>
        <Route path="reset/*" element={<Reset />} />
        <Route path="*" element={<NotFound />} />
        <Route
          path="auth"
          element={
            <PublicRouter>
              <Login />
            </PublicRouter>
          }
        />
      </Routes>
    </Suspense>
  );
}
